import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BugReportService } from '../../core/services/bug-report.service';
import { PracticeCatalogEntry } from '../../core/models/practice.model';
import { PracticeRegistryService } from '../../core/services/practice-registry.service';
import { AppSidebarDrawerService } from '../../core/services/app-sidebar-drawer.service';
import { AuthService } from '../../core/services/auth.service';
import { InterviewAvailabilityStore } from '../../core/services/interview-availability.store';
import { interviewAvailabilityAllowsRole } from '../../core/models/interview.model';
import { isProActive } from '../../core/utils/entitlements.util';
import { FeatureAnnouncementsService } from '../../core/services/feature-announcements.service';
import { GITHUB_REPO_URL } from '../../core/config/project-links';

interface LinkItem {
  key: string;
  type: 'link';
  label: string;
  to: string;
  icon?: string;
  query?: Record<string, any>;
  badge?: string | null;
}
interface GroupItem {
  type: 'group';
  label: string;
  icon?: string;
  open?: boolean;
  children: LinkItem[];
}
type NavItem = LinkItem | GroupItem;

@Component({
    selector: 'app-sidebar',
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        TooltipModule
    ],
    templateUrl: './app-sidebar.component.html',
    styleUrls: ['./app-sidebar.component.css']
})
export class AppSidebarComponent implements OnInit, OnDestroy {
  @Input() collapsed = false;
  @Input() drawerOnly = false;
  readonly githubRepoUrl = GITHUB_REPO_URL;

  private navSub?: Subscription;
  private currentPath = '/';
  private currentQuery: Record<string, any> = {};
  private readonly router = inject(Router);
  private readonly bugReport = inject(BugReportService);
  private readonly practiceRegistry = inject(PracticeRegistryService);
  private readonly drawerState = inject(AppSidebarDrawerService);
  private readonly interviewAvailability = inject(InterviewAvailabilityStore);
  readonly auth = inject(AuthService);
  readonly announcements = inject(FeatureAnnouncementsService);
  readonly isPro = computed(() => isProActive(this.auth.user()));
  readonly drawerOpen = this.drawerState.isOpen;
  readonly interviewNavVisible = signal(false);

  isLink = (i: NavItem): i is LinkItem => i.type === 'link';
  isGroup = (i: NavItem): i is GroupItem => i.type === 'group';

  nav: NavItem[] = this.buildNav(this.practiceRegistry.catalogEntries());

  private readonly syncInterviewAccessEffect = effect((onCleanup) => {
    const user = this.auth.user();
    this.interviewNavVisible.set(false);
    if (!user) return;

    const subscription = this.interviewAvailability.resolve().subscribe({
      next: (availability) => {
        this.interviewNavVisible.set(
          interviewAvailabilityAllowsRole(availability, user.role)
          || availability.activeSession !== null,
        );
      },
      error: () => this.interviewNavVisible.set(false),
    });
    onCleanup(() => subscription.unsubscribe());
  }, { allowSignalWrites: true });

  private readonly syncNavEffect = effect(() => {
    this.nav = this.buildNav(this.practiceRegistry.catalogEntries());
    this.applyDefaultOpen(this.router.url);
  });

  toggleGroup(i: number) {
    const wasCollapsed = this.collapsed;
    if (wasCollapsed) {
      this.collapsed = false;
      document.body.classList.remove('sidebar-mini');
    }
    this.nav.forEach((item, idx) => {
      if (!this.isGroup(item)) return;
      if (idx === i) {
        item.open = wasCollapsed ? true : !item.open;
      } else {
        item.open = false;
      }
    });
  }

  toggleDrawer() {
    this.drawerState.toggle();
  }

  closeDrawer() {
    this.drawerState.close();
  }

  ngOnInit(): void {
    this.applyDefaultOpen(this.router.url);
    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.applyDefaultOpen(e.urlAfterRedirects || e.url);
        this.closeDrawer();
      });
  }

  ngOnDestroy(): void {
    if (this.navSub?.unsubscribe) this.navSub.unsubscribe();
  }

  toggleCollapsed() {
    this.collapsed = !this.collapsed;
    document.body.classList.toggle('sidebar-mini', this.collapsed);
  }

  @HostListener('window:keydown.escape') onEsc() {
    if (this.drawerOpen()) this.closeDrawer();
  }

  // --- Bug modal actions ---
  openBugModal() {
    this.closeDrawer();
    this.bugReport.open({
      source: 'sidebar',
      url: typeof window !== 'undefined' ? window.location.href : this.router.url,
      route: this.router.url,
    });
  }

  shouldCollapseRail(): boolean {
    if (this.drawerOnly) return false;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return this.collapsed;
    if (window.matchMedia('(max-width: 980px)').matches) return false;
    return this.collapsed;
  }

  isGroupExpanded(index: number): boolean {
    const item = this.nav[index];
    return !!item && this.isGroup(item) && !this.shouldCollapseRail() && !!item.open;
  }

  isGroupContentHidden(index: number): boolean {
    return !this.isGroupExpanded(index);
  }

  isLinkActive(item: LinkItem): boolean {
    const path = this.currentPath;
    const view = this.queryValue('view');
    const category = this.queryValue('category');
    const isQuestionDetailRoute = /^\/(javascript|react|angular|vue|html|css)\/(coding|trivia|debug)(\/|$)/.test(path);

    switch (item.key) {
      case 'dashboard':
        return path === '/dashboard';
      case 'interview-mode':
        return path === '/interview' || path.startsWith('/interview/');
      case 'question-library':
        return (path === '/coding' || path.startsWith('/coding/') || isQuestionDetailRoute)
          && view !== 'formats'
          && category !== 'system';
      case 'incidents':
        return path === '/incidents' || path.startsWith('/incidents/');
      case 'system-design':
        return path === '/system-design'
          || path.startsWith('/system-design/')
          || (path === '/coding' && view === 'formats' && category === 'system');
      case 'tradeoff-battles':
        return path === '/tradeoffs' || path.startsWith('/tradeoffs/');
      case 'tracks':
        return path === '/tracks' || path.startsWith('/tracks/');
      case 'question-formats':
        return path === '/coding' && view === 'formats' && category !== 'system';
      case 'track-crash':
        return path === '/tracks/crash-7d';
      case 'track-foundations':
        return path === '/tracks/foundations-30d';
      case 'focus-areas':
        return path === '/focus-areas';
      case 'companies':
        return path === '/companies' || path.startsWith('/companies/');
      case 'guide-interview-blueprint':
        return path === '/guides/interview-blueprint' || path.startsWith('/guides/interview-blueprint/');
      case 'guide-framework-prep':
        return path === '/guides/framework-prep' || path.startsWith('/guides/framework-prep/');
      case 'guide-behavioral':
        return path === '/guides/behavioral' || path.startsWith('/guides/behavioral/');
      case 'guide-system-design-blueprint':
        return path === '/guides/system-design-blueprint' || path.startsWith('/guides/system-design-blueprint/');
      case 'cv-linter':
        return path === '/tools/cv' || path === '/tools/cv-linter';
      case 'changelog':
        return path === '/changelog';
      default:
        return path === item.to;
    }
  }

  private applyDefaultOpen(url: string) {
    const tree = this.router.parseUrl(url || '/');
    const primarySegments = tree.root.children['primary']?.segments ?? [];

    this.currentPath = `/${primarySegments.map((segment) => segment.path).join('/')}` || '/';
    this.currentQuery = tree.queryParams ?? {};

    const activeGroupIndex = this.nav.findIndex((item) =>
      this.isGroup(item) && item.children.some((child) => this.isLinkActive(child)),
    );

    if (activeGroupIndex >= 0) {
      this.nav.forEach((item, idx) => {
        if (!this.isGroup(item)) return;
        item.open = idx === activeGroupIndex;
      });
      return;
    }

    if (this.currentPath === '/dashboard') {
      this.nav.forEach((item, idx) => {
        if (!this.isGroup(item)) return;
        item.open = idx === 1;
      });
    }
  }

  private queryValue(key: string): string | null {
    const value = this.currentQuery[key];
    if (value === undefined || value === null) return null;
    return String(value);
  }

  private buildNav(practiceEntries: PracticeCatalogEntry[]): NavItem[] {
    return [
      { key: 'dashboard', type: 'link', label: 'Dashboard', icon: 'pi pi-th-large', to: '/dashboard' },
      {
        type: 'group',
        label: 'Practice Library',
        icon: 'pi pi-code',
        open: false,
        children: [
          ...(this.interviewNavVisible() ? [{
            key: 'interview-mode',
            type: 'link' as const,
            label: 'Timed Mock Interview',
            to: '/interview',
            icon: 'pi pi-stopwatch',
          }] : []),
          ...practiceEntries.map((entry) => ({
            key: entry.key,
            type: 'link' as const,
            label: entry.label,
            to: entry.route,
            icon: entry.icon,
            query: entry.query,
            badge: entry.badge ?? null,
          })),
        ],
      },
      {
        type: 'group',
        label: 'Study Plans',
        icon: 'pi pi-directions-alt',
        open: false,
        children: [
          { key: 'track-crash', type: 'link', label: 'Crash Track (7 days)', to: '/tracks/crash-7d', icon: 'pi pi-bolt' },
          { key: 'track-foundations', type: 'link', label: 'Foundations Track (30 days)', to: '/tracks/foundations-30d', icon: 'pi pi-calendar' },
          { key: 'focus-areas', type: 'link', label: 'Focus Areas', to: '/focus-areas', icon: 'pi pi-compass' },
          { key: 'companies', type: 'link', label: 'Company Prep', to: '/companies', icon: 'pi pi-building' },
        ],
      },
      {
        type: 'group',
        label: 'Guides',
        icon: 'pi pi-book',
        open: false,
        children: [
          { key: 'guide-interview-blueprint', type: 'link', label: 'Interview Playbook Hub', to: '/guides/interview-blueprint', icon: 'pi pi-book' },
          { key: 'guide-framework-prep', type: 'link', label: 'Framework Prep Guide', to: '/guides/framework-prep', icon: 'pi pi-compass' },
          { key: 'guide-framework-prep-js', type: 'link', label: 'JavaScript prep path', to: '/guides/framework-prep/javascript-prep-path', icon: 'pi pi-code' },
          { key: 'guide-framework-prep-react', type: 'link', label: 'React prep path', to: '/guides/framework-prep/react-prep-path', icon: 'pi pi-code' },
          { key: 'guide-framework-prep-angular', type: 'link', label: 'Angular prep path', to: '/guides/framework-prep/angular-prep-path', icon: 'pi pi-code' },
          { key: 'guide-framework-prep-vue', type: 'link', label: 'Vue prep path', to: '/guides/framework-prep/vue-prep-path', icon: 'pi pi-code' },
          { key: 'guide-system-design-blueprint', type: 'link', label: 'System Design Blueprint', to: '/guides/system-design-blueprint', icon: 'pi pi-sitemap' },
          { key: 'guide-behavioral', type: 'link', label: 'Behavioral Prep', to: '/guides/behavioral', icon: 'pi pi-users' },
        ],
      },
      {
        type: 'group',
        label: 'Shortcuts & tools',
        icon: 'pi pi-stopwatch',
        open: false,
        children: [
          { key: 'cv-linter', type: 'link', label: 'CV Linter', to: '/tools/cv', icon: 'pi pi-file-edit' },
          { key: 'changelog', type: 'link', label: 'Product changelog', to: '/changelog', icon: 'pi pi-megaphone' },
        ],
      },
    ];
  }

}
